/**
 * Loading Components
 * Spinner, Skeleton, LoadingOverlay, and PageLoading components
 * Provides visual feedback for async operations and content loading states
 */

import React, { useEffect } from 'react';
import { logger } from '@/utils/logger';

// ============================================================================
// TYPES
// ============================================================================

interface LoadingProps {
  size?: 'sm' | 'md' | 'lg';
  color?: string;
  text?: string;
}

interface SkeletonProps {
  width?: string;
  height?: string;
  rounded?: boolean;
  className?: string;
}

interface LoadingOverlayProps {
  isLoading: boolean;
  text?: string;
  fullPage?: boolean;
}

interface PageLoadingProps {
  isLoading: boolean;
  message?: string;
}

// ============================================================================
// SPINNER COMPONENT
// ============================================================================

/**
 * Animated loading spinner component
 * Displays a rotating circular spinner with optional text
 *
 * @component
 * @example
 * <Spinner size="md" text="Loading..." />
 */
export const Spinner: React.FC<LoadingProps> = ({
  size = 'md',
  color = 'white',
  text,
}) => {
  useEffect(() => {
    logger.debug(`Spinner rendered`, { size, color, text });
  }, [size, color, text]);

  const sizeClasses: Record<string, string> = {
    sm: 'h-6 w-6 border-2',
    md: 'h-10 w-10 border-3',
    lg: 'h-16 w-16 border-4',
  };

  const textSizeClasses: Record<string, string> = {
    sm: 'text-sm',
    md: 'text-base',
    lg: 'text-lg',
  };

  return (
    <div className="flex flex-col items-center justify-center gap-3">
      <div
        className={`${sizeClasses[size]} animate-spin rounded-full border-current border-t-transparent`}
        style={{ color }}
        role="status"
        aria-label="Loading"
      />
      {text && (
        <p className={`text-walrus-silver ${textSizeClasses[size]}`}>{text}</p>
      )}
    </div>
  );
};

Spinner.displayName = 'Spinner';

// ============================================================================
// SKELETON COMPONENT
// ============================================================================

/**
 * Content loading skeleton component
 * Displays a shimmering placeholder for content that's loading
 *
 * @component
 * @example
 * <Skeleton width="100%" height="20px" rounded className="mb-4" />
 */
export const Skeleton: React.FC<SkeletonProps> = ({
  width = '100%',
  height = '20px',
  rounded = false,
  className = '',
}) => {
  useEffect(() => {
    logger.debug(`Skeleton rendered`, { width, height, rounded });
  }, [width, height, rounded]);

  return (
    <div
      className={`animate-pulse bg-gradient-to-r from-walrus-charcoal via-walrus-gray to-walrus-charcoal ${
        rounded ? 'rounded-lg' : 'rounded'
      } ${className}`}
      style={{ width, height }}
      role="status"
      aria-label="Loading content"
    />
  );
};

Skeleton.displayName = 'Skeleton';

// ============================================================================
// LOADING OVERLAY COMPONENT
// ============================================================================

/**
 * Full-screen or container loading overlay
 * Displays spinner with semi-transparent backdrop
 *
 * @component
 * @example
 * <LoadingOverlay isLoading={isLoading} text="Please wait..." fullPage />
 */
export const LoadingOverlay: React.FC<LoadingOverlayProps> = ({
  isLoading,
  text = 'Loading...',
  fullPage = false,
}) => {
  useEffect(() => {
    logger.debug(`LoadingOverlay state changed`, {
      isLoading,
      fullPage,
      text,
    });
  }, [isLoading, fullPage, text]);

  if (!isLoading) return null;

  const containerClasses = fullPage
    ? 'fixed inset-0 z-50 flex items-center justify-center bg-walrus-black bg-opacity-50 backdrop-blur-sm'
    : 'absolute inset-0 flex items-center justify-center bg-walrus-charcoal bg-opacity-75';

  return (
    <div className={containerClasses} role="region" aria-busy="true">
      <Spinner size="lg" text={text} />
    </div>
  );
};

LoadingOverlay.displayName = 'LoadingOverlay';

// ============================================================================
// PAGE LOADING COMPONENT
// ============================================================================

/**
 * Full-page loading state component
 * Used for page transitions and initial data loading
 *
 * @component
 * @example
 * <PageLoading isLoading={isLoading} message="Loading page..." />
 */
export const PageLoading: React.FC<PageLoadingProps> = ({
  isLoading,
  message = 'Loading...',
}) => {
  useEffect(() => {
    logger.debug(`PageLoading state changed`, {
      isLoading,
      message,
    });

    // TODO: Add smooth transition to body overflow when loading/unloading
    if (isLoading) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }

    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [isLoading, message]);

  if (!isLoading) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-walrus-black"
      role="region"
      aria-busy="true"
      aria-label="Page loading"
    >
      <div className="flex flex-col items-center gap-6">
        <Spinner size="lg" text={message} />
        <div className="animate-pulse text-walrus-silver">
          Please wait while we load...
        </div>
      </div>
    </div>
  );
};

PageLoading.displayName = 'PageLoading';

// ============================================================================
// COMBINED LOADING COMPONENT EXPORT
// ============================================================================

/**
 * Combined loading component with all variants
 * Useful for namespace imports
 *
 * @example
 * import { Loading } from '@/components/common';
 * <Loading.Spinner size="md" />
 * <Loading.Skeleton height="100px" />
 * <Loading.PageLoading isLoading={isLoading} />
 */
export const Loading = {
  Spinner,
  Skeleton,
  LoadingOverlay,
  PageLoading,
};

export default Loading;
